import { useMemo, useState } from 'react'
import { useLocation, useSearchParams } from '@/router/navigation'
import { ErrorPage } from '@/shared/components/errors/ErrorPage'
import {
  LocalEnrollmentNavigationProvider,
  type EnrollmentNavigationState
} from '@/router/localEnrollmentNavigation'
import { InviteLinkRecoveryScreen } from './sponsoredEnrollment/InviteLinkRecoveryScreen'
import { SponsoredEnrollmentBootstrapLoading } from './sponsoredEnrollment/SponsoredEnrollmentBootstrapLoading'
import { SponsoredEnrollmentProfileBootstrap } from './sponsoredEnrollment/SponsoredEnrollmentProfileBootstrap'
import { SponsoredEnrollmentScreenRenderer } from './sponsoredEnrollment/SponsoredEnrollmentScreenRenderer'
import {
  getInitialInternalLocation,
  getSearchParamSessionToken,
  INTERNAL_PATH_ALIASES
} from './sponsoredEnrollment/routes'
import { useSponsoredEnrollmentSessionBootstrap } from './sponsoredEnrollment/useSponsoredEnrollmentSessionBootstrap'

export function SponsoredEnrollmentPage() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [profileBootstrapError, setProfileBootstrapError] = useState<string | null>(null)
  const sessionToken = getSearchParamSessionToken(searchParams)
  const effectiveSessionToken = sessionToken

  const initialLocation = useMemo(
    () => getInitialInternalLocation(location.state as EnrollmentNavigationState | null),
    [location.state]
  )
  const [isProfileBootstrapping, setIsProfileBootstrapping] = useState(
    Boolean(sessionToken && initialLocation.pathname === '/application')
  )

  useSponsoredEnrollmentSessionBootstrap({
    sessionToken,
    onBeforeBootstrap: () => {
      setProfileBootstrapError(null)
      setIsProfileBootstrapping(initialLocation.pathname === '/application')
    }
  })

  return (
    <LocalEnrollmentNavigationProvider
      canHandlePath={pathname => pathname in INTERNAL_PATH_ALIASES}
      initialLocation={initialLocation}
    >
      {effectiveSessionToken ? (
        profileBootstrapError ? (
          <ErrorPage
            code="401"
            title="Unable to load enrollment"
            message={profileBootstrapError}
            showRefresh
          />
        ) : (
          <>
            <SponsoredEnrollmentProfileBootstrap
              sessionToken={effectiveSessionToken}
              onError={setProfileBootstrapError}
              onLoadingChange={setIsProfileBootstrapping}
            />
            {isProfileBootstrapping ? (
              <SponsoredEnrollmentBootstrapLoading />
            ) : (
              <SponsoredEnrollmentScreenRenderer />
            )}
          </>
        )
      ) : (
        <InviteLinkRecoveryScreen />
      )}
    </LocalEnrollmentNavigationProvider>
  )
}
